import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Grid } from '@material-ui/core';

import RequestPageLayout from '../common/request-page-layout';
import RequestSection from '../common/request-section';
import RequestInput from '../common/request-input';
import AttachmentsField from '../common/attachments-field';
import { validateSupportTicketForm } from '../common/validators';
import { AttachmentEntityType, departmentOptions } from '../common/options';
import { createSupportTicket } from '../../../api/requests/requests';
import { getErrorMessage } from '../../../shared/services/error-handler';
import { Routes } from '../../../api/routes';

import '../Requests.scss';

const defaultFormData = {
  subject: { value: '', error: false },
  department: { value: '', error: false },
  description: { value: '', error: false },
};

const SupportTicket = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();

  const [formData, setFormData] = useState(defaultFormData);
  const [attachments, setAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async () => {
    if (!validateSupportTicketForm(formData, setFormData)) {
      enqueueSnackbar(t('fill-required-fields'), { variant: 'warning' });
      return;
    }

    setSubmitting(true);

    try {
      await createSupportTicket({
        subject: formData.subject.value,
        department: formData.department.value || undefined,
        description: formData.description.value,
        attachment_ids: attachments.map((attachment) => attachment.id),
      });

      enqueueSnackbar(t('support-ticket-submitted'), { variant: 'success' });
      history.push(Routes.myRequests);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error) || t('something-went-wrong'), {
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <RequestPageLayout
      tTitle="service-call-title"
      tSubtitle="service-call-page-subtitle"
      tSubmitLabel="open-support-ticket"
      submitClassName="service"
      onSubmit={onSubmit}
      submitting={submitting}
    >
      <RequestSection tTitle="ticket-details">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <RequestInput
              field="subject"
              tLabel="ticket-subject"
              required
              tPlaceholder="ticket-subject-placeholder"
              tError="field-is-required"
              formData={formData}
              setFormData={setFormData}
            />
          </Grid>
          <Grid item xs={12}>
            <RequestInput
              field="department"
              tLabel="target-department"
              tPlaceholder="select-target-department"
              formData={formData}
              setFormData={setFormData}
              options={departmentOptions}
            />
          </Grid>
          <Grid item xs={12}>
            <RequestInput
              field="description"
              tLabel="ticket-description"
              required
              tPlaceholder="ticket-description-placeholder"
              tError="field-is-required"
              formData={formData}
              setFormData={setFormData}
              multiline
              rows={5}
            />
          </Grid>
        </Grid>
      </RequestSection>

      <RequestSection tTitle="attachments-optional">
        <AttachmentsField
          entityType={AttachmentEntityType.supportTicket}
          attachments={attachments}
          setAttachments={setAttachments}
          tTitle="attach-file-dropzone-title"
          tHint="attach-file-dropzone-hint"
        />
      </RequestSection>
    </RequestPageLayout>
  );
};

export default SupportTicket;
